import { useTranslation } from "react-i18next";
import {
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nexus/ui";
import type { ChangelogDigest } from "@/features/changelog/api/changelog.api";
import {
  getEntryState,
  getSectionCounts,
  getTimestampDisplay,
} from "@/features/changelog/lib/changelog-display";
import { ChangelogDeliveryCell } from "./shared/ChangelogDeliveryCell";
import { ChangelogStatusBadge } from "./shared/ChangelogStatusBadge";

interface ChangelogRoundupsTableProps {
  digests: ChangelogDigest[];
  selectedId: string | null;
  onSelect: (digest: ChangelogDigest) => void;
}

export function ChangelogRoundupsTable({
  digests,
  selectedId,
  onSelect,
}: ChangelogRoundupsTableProps) {
  const { t, i18n } = useTranslation();

  return (
    <Table className="table-fixed">
      <colgroup>
        <col className="w-[200px]" />
        <col className="w-[104px]" />
        <col />
        <col className="w-[132px]" />
        <col className="w-[104px]" />
        <col className="w-9" />
      </colgroup>
      <TableHeader className="sticky top-0 z-10">
        <TableRow className="hover:bg-muted">
          <TableHead>{t("admin.changelog.roundups.table.title", "Roundup")}</TableHead>
          <TableHead>{t("admin.changelog.table.state", "State")}</TableHead>
          <TableHead>{t("admin.changelog.roundups.table.summary", "Summary")}</TableHead>
          <TableHead>{t("admin.changelog.table.delivery", "Delivery")}</TableHead>
          <TableHead>{t("admin.changelog.roundups.table.composed", "Composed")}</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {digests.map((digest) => {
          const state = getEntryState(digest);
          const composed = getTimestampDisplay(digest.createdAt, i18n.language);
          const counts = getSectionCounts(digest.sections);

          return (
            <TableRow
              key={digest.id}
              onClick={() => onSelect(digest)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelect(digest);
                }
              }}
              tabIndex={0}
              aria-label={t("admin.changelog.roundups.table.rowLabel", "Open {{title}}", {
                title: digest.title,
              })}
              data-state={selectedId === digest.id ? "selected" : undefined}
              className="cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
            >
              <TableCell className="min-w-0">
                <div className="truncate font-medium" title={digest.title}>
                  {digest.title}
                </div>
                <div className="truncate text-[11px] text-muted-foreground">
                  {t("admin.changelog.roundups.table.deployCount", {
                    count: digest.entries.length,
                  })}
                </div>
              </TableCell>

              <TableCell>
                <ChangelogStatusBadge state={state} />
              </TableCell>

              <TableCell className="min-w-0">
                <div className="truncate" title={digest.headline ?? undefined}>
                  {digest.headline ?? (
                    <span className="text-muted-foreground">
                      {state === "generating" || state === "pending"
                        ? t("admin.changelog.roundups.table.merging", "Merging picked items…")
                        : state === "stalled"
                          ? t("admin.changelog.table.stalled", "Never finished — open to retry")
                          : t("admin.changelog.table.noHeadline", "No headline")}
                    </span>
                  )}
                </div>
                <div className="truncate text-[11px] text-muted-foreground">
                  {counts
                    .map(({ key, count }) =>
                      t(`admin.changelog.table.count.${key}`, {
                        count,
                        defaultValue: `${count} ${key}`,
                      }),
                    )
                    .join(" · ")}
                </div>
              </TableCell>

              <TableCell>
                <ChangelogDeliveryCell deliveries={digest.deliveries} />
              </TableCell>

              <TableCell className="whitespace-nowrap">
                <div className="tabular-nums">{composed.time}</div>
                <div className="text-[11px] text-muted-foreground">{composed.date}</div>
              </TableCell>

              <TableCell className="px-2 text-muted-foreground">
                <Icon name="chevron-right" size="xs" color="inherit" aria-hidden />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
